/**
 * UIContext.js — Estado global de UI: modais, menus, formulários e sidebar
 *
 * Centraliza todos os estados de interface que antes ficavam em ChatInterface.js
 * e eram passados como props para ChatListPanel, ChatWindowPanel e ChatSidebar.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const UIContext = createContext(null);

export const UIProvider = ({ children }) => {
  // ── Menus ─────────────────────────────────────────────────────────────────
  const [showMenu, setShowMenu] = useState(false);
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const [showCompanyInfoMenu, setShowCompanyInfoMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [openContactMenuId, setOpenContactMenuId] = useState(null);
  const [openReactionMenuId, setOpenReactionMenuId] = useState(null);

  // ── Modais de visibilidade ─────────────────────────────────────────────────
  const [showAIModal, setShowAIModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showUpdateContactModal, setShowUpdateContactModal] = useState(false);
  const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showFlowSelector, setShowFlowSelector] = useState(false);
  const [showQuickRepliesSelector, setShowQuickRepliesSelector] = useState(false);
  const [show24HourRule, setShow24HourRule] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showUpdateGroupModal, setShowUpdateGroupModal] = useState(false);
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showPendingDeleteModal, setShowPendingDeleteModal] = useState(false);

  // ── Dados associados a modais ──────────────────────────────────────────────
  const [currentMessageForImprovement, setCurrentMessageForImprovement] = useState('');
  const [mediaViewerData, setMediaViewerData] = useState({ url: '', type: '', filename: '' });
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleteCompanyInput, setDeleteCompanyInput] = useState('');
  const [deletionInfo, setDeletionInfo] = useState(null);
  const [popupMessage, setPopupMessage] = useState(null); // { type: 'error'|'success', text: '' }

  // ── Formulário: adicionar contato ──────────────────────────────────────────
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactEmpresa, setNewContactEmpresa] = useState('');
  const [newContactCpfCnpj, setNewContactCpfCnpj] = useState('');
  const [newContactObservacao, setNewContactObservacao] = useState('');
  const [newContactBlockBotResponse, setNewContactBlockBotResponse] = useState(false);
  // Liberação do bot (evento/cortesia) para novo contato
  const [newContactBotAllowEnabled, setNewContactBotAllowEnabled] = useState(false);
  const [newContactBotAllowDuration, setNewContactBotAllowDuration] = useState('30');
  const [newContactBotAllowCustomDate, setNewContactBotAllowCustomDate] = useState('');

  // ── Formulário: atualizar contato ──────────────────────────────────────────
  const [updatedContactName, setUpdatedContactName] = useState('');
  const [updatedContactPhone, setUpdatedContactPhone] = useState('');
  const [updatedContactEmail, setUpdatedContactEmail] = useState('');
  const [updatedContactEmpresa, setUpdatedContactEmpresa] = useState('');
  const [updatedContactCpfCnpj, setUpdatedContactCpfCnpj] = useState('');
  const [updatedContactObservacao, setUpdatedContactObservacao] = useState('');
  const [updatedContactBlockBotResponse, setUpdatedContactBlockBotResponse] = useState(false);
  // Liberação do bot (evento/cortesia) para contato existente
  const [updatedContactBotAllowEnabled, setUpdatedContactBotAllowEnabled] = useState(false);
  const [updatedContactBotAllowDuration, setUpdatedContactBotAllowDuration] = useState('30');
  const [updatedContactBotAllowCustomDate, setUpdatedContactBotAllowCustomDate] = useState('');
  const [updatedContactBotAllowCurrentExpiresAt, setUpdatedContactBotAllowCurrentExpiresAt] = useState(null);
  const [updatedContactBotRuleAlreadyActive, setUpdatedContactBotRuleAlreadyActive] = useState(false);
  // ID do contato que está sendo editado — capturado no momento de abertura do modal
  // e usado em updateContact para evitar dessincronização com selectedChat.
  const [editingContactId, setEditingContactId] = useState(null);

  // ── Sidebar ────────────────────────────────────────────────────────────────
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSidebarLabels, setShowSidebarLabels] = useState(false);

  // Exibe os labels da sidebar com delay para evitar flicker durante animação
  useEffect(() => {
    if (isSidebarOpen) {
      const timer = setTimeout(() => setShowSidebarLabels(true), 300);
      return () => clearTimeout(timer);
    }
    setShowSidebarLabels(false);
  }, [isSidebarOpen]);

  // Fecha a sidebar em telas pequenas ao redimensionar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
        setShowSidebarLabels(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Helpers: reset de formulários de contato ──────────────────────────────
  const resetContactForms = useCallback(() => {
    setNewContactName('');
    setNewContactPhone('');
    setNewContactEmail('');
    setNewContactEmpresa('');
    setNewContactCpfCnpj('');
    setNewContactObservacao('');
    setNewContactBlockBotResponse(false);
    setNewContactBotAllowEnabled(false);
    setNewContactBotAllowDuration('30');
    setNewContactBotAllowCustomDate('');
    setUpdatedContactName('');
    setUpdatedContactPhone('');
    setUpdatedContactEmail('');
    setUpdatedContactEmpresa('');
    setUpdatedContactCpfCnpj('');
    setUpdatedContactObservacao('');
    setUpdatedContactBlockBotResponse(false);
    setUpdatedContactBotAllowEnabled(false);
    setUpdatedContactBotAllowDuration('30');
    setUpdatedContactBotAllowCustomDate('');
    setUpdatedContactBotAllowCurrentExpiresAt(null);
    setUpdatedContactBotRuleAlreadyActive(false);
    setEditingContactId(null);
    setShowAddContactModal(false);
    setShowUpdateContactModal(false);
    setShowDeleteContactModal(false);
    setContactToDelete(null);
  }, []);

  // ── Helpers: visualizador de mídia ─────────────────────────────────────────
  const openMediaViewer = useCallback((url, type, filename = '') => {
    setMediaViewerData({ url, type, filename });
    setShowMediaViewer(true);
  }, []);

  const closeMediaViewer = useCallback(() => {
    setShowMediaViewer(false);
    setMediaViewerData({ url: '', type: '', filename: '' });
  }, []);

  const handleMediaDownload = useCallback((url, filename = '') => {
    try {
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `arquivo_${Date.now()}`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 0);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      window.open(url, '_blank');
    }
  }, []);

  const value = {
    // Menus
    showMenu, setShowMenu,
    showMessageMenu, setShowMessageMenu,
    showCompanyInfoMenu, setShowCompanyInfoMenu,
    showEmojiPicker, setShowEmojiPicker,
    openContactMenuId, setOpenContactMenuId,
    openReactionMenuId, setOpenReactionMenuId,
    // Modais
    showAIModal, setShowAIModal,
    showAddContactModal, setShowAddContactModal,
    showUpdateContactModal, setShowUpdateContactModal,
    showDeleteContactModal, setShowDeleteContactModal,
    showTemplateSelector, setShowTemplateSelector,
    showFlowSelector, setShowFlowSelector,
    showQuickRepliesSelector, setShowQuickRepliesSelector,
    show24HourRule, setShow24HourRule,
    showCompanyModal, setShowCompanyModal,
    showObservationModal, setShowObservationModal,
    showTransferModal, setShowTransferModal,
    showCreateGroupModal, setShowCreateGroupModal,
    showUpdateGroupModal, setShowUpdateGroupModal,
    showMediaViewer, setShowMediaViewer,
    showDeleteGroupModal, setShowDeleteGroupModal,
    showDeleteAccountModal, setShowDeleteAccountModal,
    showPendingDeleteModal, setShowPendingDeleteModal,
    // Dados dos modais
    currentMessageForImprovement, setCurrentMessageForImprovement,
    mediaViewerData, setMediaViewerData,
    groupToEdit, setGroupToEdit,
    groupToDelete, setGroupToDelete,
    contactToDelete, setContactToDelete,
    deleteCompanyInput, setDeleteCompanyInput,
    deletionInfo, setDeletionInfo,
    popupMessage, setPopupMessage,
    // Formulário: novo contato
    newContactName, setNewContactName,
    newContactPhone, setNewContactPhone,
    newContactEmail, setNewContactEmail,
    newContactEmpresa, setNewContactEmpresa,
    newContactCpfCnpj, setNewContactCpfCnpj,
    newContactObservacao, setNewContactObservacao,
    newContactBlockBotResponse, setNewContactBlockBotResponse,
    newContactBotAllowEnabled, setNewContactBotAllowEnabled,
    newContactBotAllowDuration, setNewContactBotAllowDuration,
    newContactBotAllowCustomDate, setNewContactBotAllowCustomDate,
    // Formulário: atualizar contato
    updatedContactName, setUpdatedContactName,
    updatedContactPhone, setUpdatedContactPhone,
    updatedContactEmail, setUpdatedContactEmail,
    updatedContactEmpresa, setUpdatedContactEmpresa,
    updatedContactCpfCnpj, setUpdatedContactCpfCnpj,
    updatedContactObservacao, setUpdatedContactObservacao,
    updatedContactBlockBotResponse, setUpdatedContactBlockBotResponse,
    updatedContactBotAllowEnabled, setUpdatedContactBotAllowEnabled,
    updatedContactBotAllowDuration, setUpdatedContactBotAllowDuration,
    updatedContactBotAllowCustomDate, setUpdatedContactBotAllowCustomDate,
    updatedContactBotAllowCurrentExpiresAt, setUpdatedContactBotAllowCurrentExpiresAt,
    updatedContactBotRuleAlreadyActive, setUpdatedContactBotRuleAlreadyActive,
    editingContactId, setEditingContactId,
    // Sidebar
    isSidebarOpen, setIsSidebarOpen,
    showSidebarLabels, setShowSidebarLabels,
    // Helpers: reset de formulários
    resetContactForms,
    // Helpers: mídia
    openMediaViewer,
    closeMediaViewer,
    handleMediaDownload,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUIContext = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUIContext deve ser usado dentro de UIProvider');
  }
  return context;
};

export default UIContext;
